import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Tab,
  Tabs
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const HowItWorks = () => {
  const [tabValue, setTabValue] = React.useState(0);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const brandSteps = [
    {
      label: 'Create Your Brand Profile',
      description: 'Sign up and create your brand profile with details about your company, products, and target audience.',
    },
    {
      label: 'Define Your Campaign',
      description: 'Create a campaign with clear objectives, deliverables, timeline, and budget.',
    },
    {
      label: 'Discover Influencers',
      description: 'Browse through our curated list of influencers or use our matching algorithm to find the perfect fit.',
    },
    {
      label: 'Connect & Collaborate',
      description: 'Reach out to influencers, negotiate terms, and finalize the collaboration details.',
    },
    {
      label: 'Track & Analyze',
      description: 'Monitor campaign progress and analyze performance metrics to measure ROI.',
    },
  ];

  const influencerSteps = [
    {
      label: 'Create Your Influencer Profile',
      description: 'Sign up and showcase your content style, audience demographics, and previous collaborations.',
    },
    {
      label: 'Browse Opportunities',
      description: 'Explore available campaigns that match your niche and audience.',
    },
    {
      label: 'Apply for Campaigns',
      description: 'Submit applications for campaigns you\'re interested in with your proposal.',
    },
    {
      label: 'Connect & Collaborate',
      description: 'Communicate with brands, agree on deliverables, and execute the campaign.',
    },
    {
      label: 'Get Paid & Grow',
      description: 'Receive payment for your work and build your portfolio for future opportunities.',
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          pt: { xs: 8, md: 12 },
          pb: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            component="h1"
            variant="h2"
            align="center"
            color="text.primary"
            gutterBottom
            sx={{ fontWeight: 700 }}
          >
            How InfHire Works
          </Typography>
          <Typography
            variant="h5"
            align="center"
            color="text.secondary"
            paragraph
            sx={{ maxWidth: 800, mx: 'auto', mb: 6 }}
          >
            InfHire simplifies the influencer marketing process for both brands and content creators.
            Our platform streamlines discovery, collaboration, and campaign management.
          </Typography>
        </Container>
      </Box>

      {/* Tabs Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            centered
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label="For Brands" />
            <Tab label="For Influencers" />
          </Tabs>
        </Box>

        {/* Brand Content */}
        <Box role="tabpanel" hidden={tabValue !== 0}>
          {tabValue === 0 && (
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                  alt="Brand Marketing"
                  sx={{
                    width: '100%',
                    borderRadius: 2,
                    boxShadow: 3,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                  For Brands
                </Typography>
                <Typography variant="body1" paragraph>
                  InfHire helps brands find the right influencers for their marketing campaigns, 
                  streamline the collaboration process, and measure campaign performance.
                </Typography>
                
                <Stepper orientation="vertical" sx={{ mt: 4 }}>
                  {brandSteps.map((step, index) => (
                    <Step key={step.label} active={true}>
                      <StepLabel>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {step.label}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        <Typography variant="body2">{step.description}</Typography>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </Grid>
            </Grid>
          )}
        </Box>

        {/* Influencer Content */}
        <Box role="tabpanel" hidden={tabValue !== 1}>
          {tabValue === 1 && (
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80"
                  alt="Content Creator"
                  sx={{
                    width: '100%',
                    borderRadius: 2,
                    boxShadow: 3,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
                  For Influencers
                </Typography>
                <Typography variant="body1" paragraph>
                  InfHire connects influencers with relevant brand opportunities, 
                  simplifies campaign management, and ensures timely payments.
                </Typography>
                
                <Stepper orientation="vertical" sx={{ mt: 4 }}>
                  {influencerSteps.map((step, index) => (
                    <Step key={step.label} active={true}>
                      <StepLabel>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {step.label}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        <Typography variant="body2">{step.description}</Typography>
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              </Grid>
            </Grid>
          )}
        </Box>
      </Container>

      {/* Features Section */}
      <Box sx={{ bgcolor: 'background.paper', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Typography
            component="h2"
            variant="h3"
            align="center"
            color="text.primary"
            gutterBottom
            sx={{ mb: 6, fontWeight: 600 }}
          >
            Platform Features
          </Typography>

          <Grid container spacing={4}>
            {[
              {
                title: 'Smart Matching',
                description: 'Our algorithm matches brands with influencers based on audience demographics, content style, and past performance.'
              },
              {
                title: 'Secure Payments',
                description: 'Built-in payment processing ensures influencers get paid on time and brands can manage their marketing budget.'
              },
              {
                title: 'Performance Analytics',
                description: 'Track campaign metrics and ROI with detailed analytics dashboards for both brands and influencers.'
              },
              {
                title: 'Contract Management',
                description: 'Streamline the legal aspects of collaborations with customizable contract templates and e-signatures.'
              },
              {
                title: 'Content Approval',
                description: 'Simplify the content review process with in-platform approval workflows and revision requests.'
              },
              {
                title: 'Communication Tools',
                description: 'Keep all campaign communications in one place with our integrated messaging system.'
              }
            ].map((feature, index) => (
              <Grid item key={feature.title} xs={12} sm={6} md={4}>
                <Card 
                  elevation={0} 
                  sx={{ 
                    height: '100%',
                    border: '1px solid #eaeaea',
                    borderRadius: 2,
                    transition: 'transform 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: 2
                    }
                  }}
                >
                  <CardContent>
                    <Typography gutterBottom variant="h5" component="h3" sx={{ fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Ready to get started?
            </Typography>
            <Typography variant="body1" paragraph sx={{ mb: 4 }}>
              Join InfHire today and transform how you approach influencer marketing.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              component={RouterLink}
              to="/register"
              sx={{ px: 4, py: 1.5, mr: 2 }}
            >
              Sign Up Now
            </Button>
            <Button
              variant="outlined"
              color="primary"
              size="large"
              component={RouterLink}
              to="/pricing"
              endIcon={<ArrowForwardIcon />}
              sx={{ px: 4, py: 1.5 }}
            >
              View Pricing
            </Button>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default HowItWorks;
